/* eslint-disable @typescript-eslint/no-explicit-any */
import { Toast } from "primereact/toast";
import { trackAssetFormOutcome } from "../../analytics";
import { useEffect, useRef, useState } from "react";
import { useHomeContext } from "../../contexts/HomeContext";
import useAuthHttp from "../../hooks/useAuthHttp";
import View from "../Container";
import Nav, { NavItem } from "../Nav";
import Button from "../Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import { IAssetDetailsResponse, IAssetInfo, IAssetTypesResponse, AssetMainType } from "../../models/asset";
import { useMutation, useQuery } from "@tanstack/react-query";
import { groupAssetListTypeByGroupName } from "../../commons/group-assets-list-type";
import { ASSET_MIN_PURCHASE_DATE, yupAssetPurchaseDate } from "../../commons/assetPurchaseDate";
import { IDebentureSearch } from "../../hooks/useFindDebenture";
import DebentureSearchSelector from "../DebentureSearchSelector";
import FormField from "../FormField";
import { InputNumber } from "primereact/inputnumber";
import BankSearchSelector from "../BankSearchSelector";
import { Calendar } from "primereact/calendar";
import Label from "../Label";
import Show from "../Show";
import MainLoader from "../../layout/MainLoader";
import Container from "../Container";
import { IExtractedInvestment } from "../../models/extractedData";
import { assetSourceFieldForDirpfImport } from "../../commons/dirpfImportMeta";
import { useExchangeRateByType } from "../../hooks/useExchangeRateByType";
import MissingQuoteNotice from "../MissingQuoteNotice";
import { isMissingQuote } from "../../commons/missing-quote";
import { formatDebentureYield } from "../../commons/debenture-yield";
import Text from "../Text";

interface IDebentureHandleForm {
    debenture?: IDebentureSearch;
    negotiatingBank?: {
        id: string;
        name: string;
    };
    purchaseDate?: Date;
    quantity?: number;
    operationCosts?: {
        brokerage?: number;
        others?: number;
    };
    cotation?: number;
}

interface IDebentureHandleFormProps {
    onSuccessRegister(assetInfo: IDebentureHandleForm | null): void;
    goBack(): void;
    assetId?: string;
    initialData?: IExtractedInvestment;
}

const DebentureHandleForm = (props: IDebentureHandleFormProps) => {
    const toast = useRef<Toast | null>(null);
    const homeContext = useHomeContext();
    const http = useAuthHttp();
    const [isEdit, setIsEdit] = useState(false);

    const assetListTypeQuery = useQuery({
        queryKey: [`assetListType`],
        queryFn: async () =>
            await http.doGet<IAssetTypesResponse>('asset/list_types')
    });
    const assetListTypeGrouped = groupAssetListTypeByGroupName(assetListTypeQuery.data?.asset_list ?? []);

    const insertAssetMutation = useMutation({
        mutationFn: async (form: IDebentureHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped
                    .find(listType => listType.groupName == "Debênture")
                    ?.names[0]
                    .id ?? "",
                asset_type: "DB",
                asset_due_date: moment(form.debenture?.dueDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_fin_negotiating_id: form.negotiatingBank?.id,
                asset_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_db_details: {
                    db_code: form.debenture?.code,
                    db_quantity_prch: form.quantity,
                    db_pu_value_prch: form.cotation,
                    db_oc_brokerage: form.operationCosts?.brokerage,
                    db_oc_others: form.operationCosts?.others
                },
                ...(assetSourceFieldForDirpfImport(props.initialData) ?? {})
            };

            return await http.doPost<any, IAssetInfo>("/asset/insert", body);
        }
    });

    const updateAssetMutation = useMutation({
        mutationFn: async (form: IDebentureHandleForm) => {
            const body = {
                asset_id: assetListTypeGrouped
                    .find(listType => listType.groupName == "Debênture")
                    ?.names[0]
                    .id ?? "",
                asset_type: "DB",
                asset_due_date: moment(form.debenture?.dueDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_fin_negotiating_id: form.negotiatingBank?.id,
                asset_purchase_date: moment(form.purchaseDate).format("YYYY-MM-DD HH:mm:ss"),
                asset_db_details: {
                    db_code: form.debenture?.code,
                    db_quantity_prch: form.quantity,
                    db_pu_value_prch: form.cotation,
                    db_oc_brokerage: form.operationCosts?.brokerage,
                    db_oc_others: form.operationCosts?.others
                }
            };

            return await http.doPatch<any, IAssetInfo>(`/asset/${props.assetId}`, body);
        }
    });

    const formik = useFormik<IDebentureHandleForm>({
        initialValues: {
            debenture: undefined,
            negotiatingBank: undefined,
            purchaseDate: undefined,
            quantity: 0,
            operationCosts: {
                brokerage: 0,
                others: 0
            },
            cotation: 0
        },
        validationSchema: Yup
            .object()
            .shape({
                debenture: Yup
                    .object()
                    .required("Campo obrigatório"),
                negotiatingBank: Yup
                    .object()
                    .required("Campo obrigatório"),
                purchaseDate: yupAssetPurchaseDate(),
                quantity: Yup
                    .number()
                    .min(1, "Valor mínimo obrigatório")
                    .required("Campo obrigatório"),
                operationCosts: Yup
                    .object()
                    .shape({
                        brokerage: Yup
                            .number()
                            .optional(),
                        others: Yup
                            .number()
                            .optional()
                    }),
                cotation: Yup
                    .number()
                    .min(0.1, "Valor mínimo obrigatório")
                    .required("Campo obrigatório")
            }),
        onSubmit: async (form) => await send(form)
    });

    const skipNextQuoteFill = useRef(!!props.assetId || !!(props.initialData?.asset_db_details?.db_pu_value_prch));

    const { exchangeRateQuery } = useExchangeRateByType(
        AssetMainType.Debenture,
        formik.values.purchaseDate,
        formik.values.debenture?.code,
        (cotation) => {
            if (skipNextQuoteFill.current) {
                skipNextQuoteFill.current = false;
                return;
            }
            formik.setFieldValue("cotation", cotation);
        },
    );
    const missingQuote = isMissingQuote(exchangeRateQuery.data) ? exchangeRateQuery.data : null;

    const assetDetailsQuery = useQuery({
        queryKey: ["assetDetails", props.assetId],
        gcTime: 0,
        queryFn: async () => {
            if (!props.assetId) {
                setIsEdit(false);
                return {
                    asset_info: null
                };
            }

            setIsEdit(true);
            return await http.doGet<IAssetDetailsResponse>(`/asset/${props.assetId}`)
        }
    });

    useEffect(() => {
        const body = document.getElementsByTagName("body")[0];
        body.style.overflow = "hidden";

        return () => {
            body.style.overflow = "auto";
        }
    }, []);

    useEffect(() => {
        if (!assetDetailsQuery.data?.asset_info) {
            return;
        }
        const assetDetails = assetDetailsQuery.data;
        formik.setValues({
            debenture: {
                id: assetDetails.asset_info.asset_db_details.debenture.id,
                code: assetDetails.asset_info.asset_db_details.debenture.deb_code,
                cotation: [],
                dueDate: moment(assetDetails.asset_info.asset_db_details.debenture.deb_due_date).toDate(),
                index: assetDetails.asset_info.asset_db_details.debenture.deb_index,
                interestRate: assetDetails.asset_info.asset_db_details.debenture.deb_interest_rate
            },
            negotiatingBank: {
                id: assetDetails.asset_info.asset_fin_issue.id,
                name: assetDetails.asset_info.asset_fin_issue.inst_name
            },
            purchaseDate: moment(assetDetails.asset_info.asset_purchase_date).toDate(),
            quantity: assetDetails.asset_info.asset_db_details.db_quantity_prch,
            operationCosts: {
                brokerage: assetDetails.asset_info.asset_db_details.db_oc_brokerage,
                others: assetDetails.asset_info.asset_db_details.db_oc_others
            },
            cotation: assetDetails.asset_info.asset_db_details.db_pu_value_prch
        });
    }, [assetDetailsQuery.data]);

    useEffect(() => {
        if (!props.initialData?.asset_db_details) {
            return;
        }
        const data = props.initialData;
        formik.setValues({
            debenture: {
                id: data.asset_db_details?.debenture?.id || "",
                code: data.asset_db_details?.debenture?.deb_code || "",
                cotation: [],
                dueDate: moment(data.asset_db_details?.debenture?.deb_due_date).toDate(),
                index: data.asset_db_details?.debenture?.deb_index,
                interestRate: data.asset_db_details?.debenture?.deb_interest_rate
            },
            purchaseDate: data.asset_purchase_date ? moment(data.asset_purchase_date).toDate() : undefined,
            quantity: data.asset_db_details?.db_quantity_prch || 0,
            cotation: data.asset_db_details?.db_pu_value_prch || 0,
            negotiatingBank: data.asset_fin_issue ? {
                id: data.asset_fin_issue.id,
                name: data.asset_fin_issue.inst_name
            } : undefined
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.initialData]);

    const upsert = async (assetPayload: IDebentureHandleForm) => {
        if (isEdit) return await updateAssetMutation.mutateAsync(assetPayload);

        return await insertAssetMutation.mutateAsync(assetPayload);
    }
   
    const send = async (form: IDebentureHandleForm) => {
        if (!form) {
            return;
        }

        try {
            const assetInfo = await upsert(form);

            if (assetInfo) {
                props.onSuccessRegister(form);
                await homeContext.refresh();
            } else {
                if (!isEdit) trackAssetFormOutcome('debenture', false, 'failed');
                toast.current?.show({
                    severity: "error",
                    summary: "Falha ao cadastrar investimento"
                });
            }
        } catch (err) {
            if (!isEdit) trackAssetFormOutcome('debenture', false, 'failed');
            toast.current?.show({
                severity: "error",
                summary: (err as Error).message
            });
        }
    }

    return (
        <>
            <Toast ref={toast} />
            <Show when={assetDetailsQuery.isLoading}>
                <Container center fullWidth>
                    <MainLoader fullHeight={false} minHeight="300px" />
                </Container>
            </Show>

            <Show when={!assetDetailsQuery.isLoading}>
                <View flexColumn gap={3}>
                    <h2 className="m-0">Debênture</h2>

                    <Nav>
                        <NavItem
                            id="go-back"
                            showPrefixIcon
                            onSelectOption={() => props.goBack()}
                        >
                            Voltar
                        </NavItem>
                    </Nav>

                    <DebentureSearchSelector
                        selectedDebenture={formik.values.debenture}
                        selectDebenture={(debenture) => {
                            skipNextQuoteFill.current = false;
                            formik.setFieldValue("debenture", debenture);
                        }}
                        error={formik.errors.debenture}
                        hasError={!!formik.errors.debenture && formik.touched.debenture}
                    />

                    {formik.values.debenture?.code ? (
                        <Text>{formatDebentureYield(formik.values.debenture.index, formik.values.debenture.interestRate)}</Text>
                    ) : null}

                    <BankSearchSelector
                        hasError={!!formik.errors.negotiatingBank && formik.touched.negotiatingBank}
                        error={formik.errors.negotiatingBank}
                        selected={formik.values.negotiatingBank}
                        label="Onde você comprou?"
                        onSelectBank={(bank) => {
                            formik.setFieldValue("negotiatingBank", !bank ? null : { id: bank?._id, name: bank?.financial_inst_name });
                        }}
                    />

                    <FormField
                        id="purchaseDate"
                        label="Quando você comprou?"
                        hasError={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                        error={formik.errors.purchaseDate}
                    >
                        <Calendar
                            invalid={!!formik.errors.purchaseDate && formik.touched.purchaseDate}
                            value={formik.values.purchaseDate}
                            dateFormat="dd/mm/yy"
                            showIcon
                            readOnlyInput
                            minDate={ASSET_MIN_PURCHASE_DATE}
                            disabledDays={[0, 6]}
                            onChange={(event) => {
                                skipNextQuoteFill.current = false;
                                formik.setFieldValue("purchaseDate", event.value)
                            }}
                            locale="pt"
                        />
                    </FormField>

                    <FormField
                        id="quantity"
                        hasError={!!formik.errors.quantity && formik.touched.quantity}
                        error={formik.errors.quantity}
                        label="Quantidade"
                    >
                        <InputNumber
                            invalid={!!formik.errors.quantity && formik.touched.quantity}
                            value={formik.values.quantity}
                            minFractionDigits={2}
                            maxFractionDigits={10}
                            onValueChange={(e) =>
                                formik.setFieldValue("quantity", e.value)
                            }
                            locale="pt-BR"
                        />
                    </FormField>

                    <View flexColumn gap={2}>
                        <Label>Corretagem</Label>
                        <InputNumber
                            value={formik.values.operationCosts?.brokerage}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, brokerage: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <View flexColumn gap={2}>
                        <Label>Outros custos</Label>
                        <InputNumber
                            value={formik.values.operationCosts?.others}
                            onValueChange={(e) =>
                                formik.setFieldValue("operationCosts", { ...formik.values.operationCosts, others: e.value })
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </View>

                    <FormField
                        id="cotation"
                        hasError={!!formik.errors.cotation && formik.touched.cotation}
                        error={formik.errors.cotation}
                        label="Preço que você pagou por unidade (PU)"
                    >
                        <InputNumber
                            invalid={!!formik.errors.cotation && formik.touched.cotation}
                            value={formik.values.cotation}
                            onValueChange={(e) =>
                                formik.setFieldValue("cotation", e.value)
                            }
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                            minFractionDigits={formik.values.cotation?.toString().split(".")[1]?.length ?? 4}
                        />
                    </FormField>

                    {missingQuote ? (
                        <MissingQuoteNotice
                            info={missingQuote}
                            onUseFirstDate={missingQuote.first_available_date ? () => {
                                skipNextQuoteFill.current = false;
                                formik.setFieldValue(
                                    "purchaseDate",
                                    moment(missingQuote.first_available_date as string, "YYYY-MM-DD").toDate()
                                );
                            } : undefined}
                        />
                    ) : null}

                    <FormField
                        id="totalValue"
                        label="Valor total de compra"
                    >
                        <InputNumber
                            disabled
                            value={((formik.values?.cotation ?? 0) + (formik.values?.operationCosts?.brokerage ?? 0) + (formik.values?.operationCosts?.others ?? 0)) * (formik.values.quantity ?? 0)}
                            mode="currency"
                            currency="BRL"
                            locale="pt-BR"
                        />
                    </FormField>

                    <View fullWidth>
                        <Button
                            onClick={() => {
                                formik.handleSubmit();
                                return Promise.resolve();
                            }}
                            className="w-full justify-content-center"
                            loading={insertAssetMutation.isPending}
                            disabled={insertAssetMutation.isPending}
                        >
                            {isEdit ? "Editar" : "Cadastrar"}
                        </Button>
                    </View>
                </View>
            </Show> 
        </>
    );
}

export default DebentureHandleForm;